// Grid geometry shared by the dashboard and the public share page.
// react-grid-layout v2 takes these through gridConfig, not top-level props.
export const GRID_COLS = 12;
export const ROW_HEIGHT = 30;

export const DESKTOP_GRID_CONFIG = {
  cols: GRID_COLS,
  rowHeight: ROW_HEIGHT,
  margin: [10, 10],
  containerPadding: [10, 10],
};

export const MOBILE_GRID_CONFIG = {
  cols: GRID_COLS,
  rowHeight: ROW_HEIGHT,
  margin: [0, 8],
  containerPadding: [8, 8],
};

// Clicks on these never start a drag
export const NO_DRAG_CLASS = "widget-no-drag";

const MAP_WIDGET_ID = 3;
const MOBILE_MAP_HEIGHT = 6;

// Starting size for a newly added widget, by widget type id
const DEFAULT_SIZES = {
  2: { w: 3, h: 5 },
  3: { w: 8, h: 10 },
  5: { w: 12, h: 2 },
  6: { w: 4, h: 6 },
  7: { w: 3, h: 4 },
  8: { w: 12, h: 1 },
  9: { w: 3, h: 8 },
  10: { w: 6, h: 8 },
  11: { w: 3, h: 4 },
  12: { w: 6, h: 10 },
  13: { w: 4, h: 8 },
};

const FALLBACK_SIZE = { w: 4, h: 4 };

export const getDefaultWidgetSize = (widgetTypeId) =>
  DEFAULT_SIZES[parseInt(widgetTypeId)] || FALLBACK_SIZE;

const byPosition = (a, b) => a.y - b.y || a.x - b.x;

/**
 * Layout as stored on the dashboard's widgets
 */
export const widgetsToLayout = (widgets = []) =>
  widgets.map((widget) => ({
    i: String(widget.id),
    x: widget.x ?? 0,
    y: widget.y ?? 0,
    w: Math.max(widget.w || 1, 1),
    h: Math.max(widget.h || 1, 1),
  }));

/**
 * Single-column layout for phones, keeping the desktop reading order
 */
export const stackLayoutForMobile = (widgets = []) => {
  let y = 0;
  return [...widgets]
    .sort((a, b) => byPosition(a ?? {}, b ?? {}))
    .map((widget) => {
      const h =
        widget.widget_id === MAP_WIDGET_ID
          ? MOBILE_MAP_HEIGHT
          : Math.max(widget.h || 1, 1);
      const item = { i: String(widget.id), x: 0, y, w: GRID_COLS, h };
      y += h;
      return item;
    });
};

/**
 * Keep only what the API stores, so dirty checks and saves ignore grid internals
 */
export const normaliseLayout = (layout = []) =>
  layout
    .map(({ i, x, y, w, h }) => ({ i: String(i), x, y, w, h }))
    .sort((a, b) => a.i.localeCompare(b.i));

export const layoutsEqual = (a = [], b = []) => {
  const left = normaliseLayout(a);
  const right = normaliseLayout(b);
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) =>
      item.i === right[index].i &&
      item.x === right[index].x &&
      item.y === right[index].y &&
      item.w === right[index].w &&
      item.h === right[index].h
  );
};

/**
 * New widgets start on a fresh row below the lowest widget
 */
export const nextWidgetPosition = (widgets = []) => {
  const bottom = widgets.reduce(
    (max, widget) => Math.max(max, (widget.y ?? 0) + (widget.h ?? 0)),
    0
  );
  return { x: 0, y: bottom };
};
